import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './MenuAdmin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const toIST = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// Converts a date value from the API (may be ISO timestamp or YYYY-MM-DD string)
// to the YYYY-MM-DD string that <input type="date"> expects, using local time parts
// to avoid the UTC-midnight-to-local-date off-by-one shift.
const toDateInput = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr; // already plain date
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
};

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const slugify = (s) =>
  s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

function buildTree(flat, parentId = null) {
  return flat
    .filter((m) => (parentId === null ? m.parent_id == null : m.parent_id === parentId))
    .map((m) => ({ ...m, children: buildTree(flat, m.id) }))
    .sort((a, b) => (a.display_order - b.display_order) || a.menu_name.localeCompare(b.menu_name));
}

const TODAY = new Date().toISOString().split('T')[0];

/* ═══════════════════════════════════════════════════════════
   Menu Modal
   ═══════════════════════════════════════════════════════════ */
const MenuModal = ({ initial, parentId, parentName, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial ? { ...initial } :
    { menu_name: '', menu_slug: '', menu_code: '', description: '', image_url: '', display_order: 0, is_active: true, header_text: '' }
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const setField = (field, val) =>
    setForm((f) => ({
      ...f,
      [field]: val,
      ...(field === 'menu_name' && !isEdit ? { menu_slug: slugify(val) } : {}),
    }));

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const data = new FormData();
      data.append('image', file);
      const res = await axios.post(`${API_URL}/menu-admin/upload-image`, data, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setField('image_url', res.data.url);
    } catch (err) {
      setError(err.response?.data?.error || 'Image upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.menu_name.trim()) { setError('Menu name is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, parent_id: parentId ?? null };
      if (isEdit) {
        await axios.put(`${API_URL}/menu-admin/menus/${initial.id}`, payload, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API_URL}/menu-admin/menus`, payload, { headers: getAuthHeader() });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save menu.');
    } finally {
      setSaving(false);
    }
  };

  const imagePreviewSrc = form.image_url
    ? (form.image_url.startsWith('http') ? form.image_url : `${API_URL}${form.image_url}`)
    : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? `Edit — ${initial.menu_name}` : `Add Menu${parentName ? ` under "${parentName}"` : ' (Root)'}`}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠</span>{error}
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input className="form-input" value={form.menu_name} onChange={(e) => setField('menu_name', e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Slug</label>
              <input className="form-input" value={form.menu_slug} onChange={(e) => setField('menu_slug', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>
              Campaign Code{' '}
              <span style={{ fontWeight: 400, opacity: 0.65 }}>(unique, used in public landing URL)</span>
            </label>
            <input
              className="form-input"
              value={form.menu_code || ''}
              onChange={(e) => setField('menu_code', e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
              placeholder="e.g. summer-sale"
              maxLength={50}
            />
            {form.menu_code && (
              <span style={{ fontSize: 12, color: '#6b7280', marginTop: 4, display: 'block' }}>
                Public URL: <code>/menu/{form.menu_code}</code>
              </span>
            )}
          </div>
          <div className="form-group">
            <label>Header Text <span style={{ fontWeight: 400, opacity: 0.65 }}>(shown above tile on Home &amp; Categories)</span></label>
            <input className="form-input" type="text" maxLength={100} value={form.header_text || ''} onChange={(e) => setField('header_text', e.target.value)} placeholder="e.g. NEW" />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-input form-textarea" rows={2} value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Image</label>
              <div className="img-upload-area">
                {imagePreviewSrc && (
                  <div className="img-preview-wrap">
                    <img src={imagePreviewSrc} alt="preview" className="img-preview" />
                    <button type="button" className="img-clear-btn" onClick={() => setField('image_url', '')} title="Remove image">×</button>
                  </div>
                )}
                <label className={`img-upload-btn${uploading ? ' img-upload-btn--loading' : ''}`}>
                  {uploading ? 'Uploading…' : imagePreviewSrc ? '🔄 Replace' : '📷 Upload Image'}
                  <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={handleImageChange} disabled={uploading} />
                </label>
                {form.image_url && (
                  <span className="img-url-hint" title={form.image_url}>{form.image_url.split('/').pop()}</span>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Display Order</label>
              <input className="form-input" type="number" value={form.display_order} onChange={(e) => setField('display_order', parseInt(e.target.value) || 0)} />
            </div>
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setField('is_active', e.target.checked)} />
              Active
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving || uploading}>
              {saving ? 'Saving…' : isEdit ? 'Update Menu' : 'Create Menu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Product Search (autocomplete LOV)
   ═══════════════════════════════════════════════════════════ */
const ProductSearch = ({ productsLov, value, onChange }) => {
  const selected = productsLov.find((p) => p.id === parseInt(value, 10));
  const [query, setQuery] = useState(selected?.name || '');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = query.trim()
    ? productsLov.filter((p) => p.name.toLowerCase().includes(query.toLowerCase())).slice(0, 10)
    : productsLov.slice(0, 10);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (product) => {
    onChange(product.id);
    setQuery(product.name);
    setOpen(false);
    setCursor(-1);
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setCursor(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!open) { if (e.key === 'ArrowDown') setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (cursor >= 0 && filtered[cursor]) select(filtered[cursor]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  const previewSrc = selected?.image_url
    ? (selected.image_url.startsWith('http') ? selected.image_url : `${API_URL}${selected.image_url}`)
    : null;

  return (
    <div ref={containerRef} className="ps-wrap">
      <div className="ps-input-row">
        {previewSrc
          ? <img src={previewSrc} alt={selected.name} className="ps-thumb" />
          : <span className="ps-thumb ps-thumb--empty">P</span>}
        <input
          ref={inputRef}
          className="form-input ps-input"
          value={query}
          placeholder="Type to search products…"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setCursor(-1);
            if (!e.target.value) onChange('');
          }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        {value && (
          <button type="button" className="ps-clear" onClick={clear} title="Clear">×</button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <ul className="ps-dropdown">
          {filtered.map((p, i) => {
            const src = p.image_url
              ? (p.image_url.startsWith('http') ? p.image_url : `${API_URL}${p.image_url}`)
              : null;
            return (
              <li
                key={p.id}
                className={`ps-option${i === cursor ? ' ps-option--active' : ''}`}
                onMouseDown={() => select(p)}
                onMouseEnter={() => setCursor(i)}
              >
                {src
                  ? <img src={src} alt={p.name} className="ps-opt-img" />
                  : <span className="ps-opt-placeholder">P</span>}
                <span className="ps-opt-name">{p.name}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Product Link Modal
   ═══════════════════════════════════════════════════════════ */
const LinkModal = ({ menuId, menuName, initial, productsLov, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial ? {
      description: initial.description || '',
      display_order: initial.display_order,
      is_active: initial.is_active,
      root_menu_id: initial.root_menu_id || '',
      referral_commission_percent: initial.referral_commission_percent ?? '',
    } : { product_id: '', description: '', display_order: 0, is_active: true, root_menu_id: '', referral_commission_percent: '' }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !form.product_id) { setError('Select a product.'); return; }
    setSaving(true); setError('');
    try {
      const commissionVal = form.referral_commission_percent !== '' ? parseFloat(form.referral_commission_percent) : null;
      if (isEdit) {
        await axios.put(`${API_URL}/menu-admin/menu-products/${initial.id}`, {
          ...form,
          root_menu_id: form.root_menu_id ? parseInt(form.root_menu_id) : null,
          referral_commission_percent: commissionVal,
        }, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API_URL}/menu-admin/menu-products`, {
          ...form,
          menu_id: menuId,
          product_id: parseInt(form.product_id),
          root_menu_id: form.root_menu_id ? parseInt(form.root_menu_id) : null,
          referral_commission_percent: commissionVal,
        }, { headers: getAuthHeader() });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save product link.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? `Edit Link — ${initial.product_name}` : `Link Product to "${menuName}"`}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error"><span className="alert-icon">⚠</span>{error}</div>}
          {!isEdit && (
            <div className="form-group">
              <label>Product *</label>
              <ProductSearch
                productsLov={productsLov}
                value={form.product_id}
                onChange={(id) => setForm((f) => ({ ...f, product_id: id }))}
              />
            </div>
          )}
          <div className="form-group">
            <label>Description</label>
            <input className="form-input" value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Display Order</label>
              <input className="form-input" type="number" value={form.display_order}
                onChange={(e) => setForm((f) => ({ ...f, display_order: parseInt(e.target.value) || 0 }))} />
            </div>
            <div className="form-group">
              <label>Root Menu ID</label>
              <input className="form-input" type="number" value={form.root_menu_id}
                readOnly style={{ background: '#f5f5f5', cursor: 'not-allowed', color: '#888' }} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Commission %</label>
              <input className="form-input" type="number" step="0.01" min="0" max="100"
                placeholder="e.g. 5"
                value={form.referral_commission_percent}
                onChange={(e) => setForm((f) => ({ ...f, referral_commission_percent: e.target.value }))} />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label className="checkbox-label">
                <input type="checkbox" checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
                Active
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update Link' : 'Add Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Price Modal
   ═══════════════════════════════════════════════════════════ */
const PriceModal = ({ initial, menuProductId, menuPackId, productName, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial ? { ...initial, start_date: toDateInput(initial.start_date), end_date: toDateInput(initial.end_date), order_cutoff_time: initial.order_cutoff_time || '' }
    : { price: '', start_date: TODAY, end_date: '', is_available: true, order_cutoff_time: '', order_after_cutoff: 'N', order_lead_days: 0, days_available: 'AD' }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.price || !form.start_date) { setError('Price and start date are required.'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/menu-admin/prices/${initial.id}`, form, { headers: getAuthHeader() });
      } else {
        const linkField = menuProductId ? { menu_product_id: menuProductId } : { menu_pack_id: menuPackId };
        await axios.post(`${API_URL}/menu-admin/prices`, { ...form, ...linkField }, { headers: getAuthHeader() });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save price.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Price' : `Add Price — ${productName}`}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error"><span className="alert-icon">⚠</span>{error}</div>}
          <div className="form-row">
            <div className="form-group">
              <label>Price (₹) *</label>
              <input className="form-input" type="number" step="0.01" min="0" value={form.price}
                onChange={(e) => set('price', e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Start Date *</label>
              <input className="form-input" type="date" value={form.start_date}
                onChange={(e) => set('start_date', e.target.value)} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>End Date</label>
              <input className="form-input" type="date" value={form.end_date}
                onChange={(e) => set('end_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Order Cutoff Time</label>
              <input className="form-input" type="time" value={form.order_cutoff_time}
                onChange={(e) => set('order_cutoff_time', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Lead Days</label>
              <input className="form-input" type="number" min="0" value={form.order_lead_days}
                onChange={(e) => set('order_lead_days', parseInt(e.target.value) || 0)} />
            </div>
            <div className="form-group">
              <label>Days Available</label>
              <input className="form-input" placeholder="Mon,Tue,Wed…" value={form.days_available || ''}
                onChange={(e) => set('days_available', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" checked={form.is_available}
                  onChange={(e) => set('is_available', e.target.checked)} />
                Available
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" checked={form.order_after_cutoff === 'Y'}
                  onChange={(e) => set('order_after_cutoff', e.target.checked ? 'Y' : 'N')} />
                Order After Cutoff
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update Price' : 'Add Price'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Root Products Panel
   ═══════════════════════════════════════════════════════════ */
const RootProductsPanel = ({ rootMenu, onClose }) => {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [originals, setOriginals] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API_URL}/menu-admin/root-menu-products`,
        { params: { root_menu_id: rootMenu.id }, headers: getAuthHeader() });
      setRows(res.data);
      const orig = {};
      res.data.forEach((r) => { orig[r.id] = { display_order: r.display_order, is_active: r.is_active }; });
      setOriginals(orig);
      setEdits({});
    } catch {
      setError('Failed to load products.');
    } finally {
      setLoading(false);
    }
  }, [rootMenu.id]);

  useEffect(() => { load(); }, [load]);

  const setEdit = (id, field, value) =>
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] ?? originals[id]), [field]: value } }));

  const isChanged = (id) => {
    const e = edits[id];
    if (!e) return false;
    const o = originals[id];
    return e.display_order !== o?.display_order || e.is_active !== o?.is_active;
  };

  const changedUpdates = Object.entries(edits)
    .filter(([id]) => isChanged(id))
    .map(([id, e]) => ({ id: parseInt(id), display_order: parseInt(e.display_order) || 0, is_active: e.is_active }));

  const handleUpdate = async () => {
    if (changedUpdates.length === 0) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      await axios.put(`${API_URL}/menu-admin/menu-products-batch`,
        { updates: changedUpdates }, { headers: getAuthHeader() });
      setSuccess(`Updated ${changedUpdates.length} record${changedUpdates.length !== 1 ? 's' : ''}.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const cur = (id, field, fallback) =>
    edits[id]?.[field] !== undefined ? edits[id][field] : fallback;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="rmp-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Products — {rootMenu.menu_name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="alert alert-error" style={{ margin: '12px 24px 0' }}><span className="alert-icon">⚠</span>{error}</div>}
        {success && <div className="alert alert-success" style={{ margin: '12px 24px 0' }}><span className="alert-icon">✓</span>{success}</div>}

        {loading ? (
          <div className="loading-spinner" style={{ padding: 40 }}>Loading…</div>
        ) : (
          <>
            <div className="rmp-table-wrap">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Menu</th>
                    <th style={{ width: 110 }}>Display Order</th>
                    <th style={{ width: 110 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={4} className="no-data">No products under this root menu.</td></tr>
                  ) : rows.map((r) => {
                    const dispOrder = cur(r.id, 'display_order', r.display_order);
                    const isActive = cur(r.id, 'is_active', r.is_active);
                    return (
                      <tr key={r.id} className={isChanged(r.id) ? 'rmp-row--changed' : ''}>
                        <td>
                          <div className="user-info">
                            {r.product_image_url
                              ? <img src={resolveImg(r.product_image_url)} alt={r.product_name} className="mn-prod-img" />
                              : <span className="user-avatar">P</span>}
                            <span>{r.product_name}</span>
                          </div>
                        </td>
                        <td style={{ fontSize: '0.82rem', color: '#666' }}>{r.menu_name}</td>
                        <td>
                          <input
                            type="number"
                            className="rmp-order-input"
                            value={dispOrder}
                            onChange={(e) => setEdit(r.id, 'display_order', parseInt(e.target.value) || 0)}
                          />
                        </td>
                        <td>
                          <label className="checkbox-label" style={{ fontSize: '0.85rem' }}>
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={(e) => setEdit(r.id, 'is_active', e.target.checked)}
                            />
                            {isActive ? 'Active' : 'Inactive'}
                          </label>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="rmp-footer">
              {changedUpdates.length > 0 && (
                <span className="rmp-changed-count">
                  {changedUpdates.length} unsaved change{changedUpdates.length !== 1 ? 's' : ''}
                </span>
              )}
              <button className="btn-cancel" onClick={onClose}>Close</button>
              <button
                className="btn-submit"
                onClick={handleUpdate}
                disabled={saving || changedUpdates.length === 0}
              >
                {saving ? 'Updating…' : `Update${changedUpdates.length > 0 ? ` (${changedUpdates.length})` : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Tree Node
   ═══════════════════════════════════════════════════════════ */
const TreeNode = ({ node, expanded, selectedId, onToggle, onEdit, onAddChild, onProducts, onRootProducts, onDelete, depth }) => {
  const isExpanded = expanded.has(node.id);
  const isSelected = selectedId === node.id;
  const hasChildren = node.children && node.children.length > 0;
  const label = (node.header_text && node.header_text.trim()) ? node.header_text.trim() : node.menu_name;

  return (
    <div className="mn-node">
      <div
        className={`mn-node-row ${isSelected ? 'mn-node-row--selected' : ''}`}
        style={{ paddingLeft: `${0.75 + depth * 1.25}rem` }}
      >
        <button className="mn-toggle" onClick={() => hasChildren && onToggle(node.id)}>
          {hasChildren ? (isExpanded ? '▼' : '▶') : '·'}
        </button>
        <div className="mn-icon">
          {node.image_url
            ? <img src={resolveImg(node.image_url)} alt={label} />
            : <span>&#128193;</span>}
        </div>
        <span className="mn-name" title={label} onClick={() => onProducts(node)}>
          {label.length > 15 ? label.slice(0, 15) + '…' : label}
        </span>
        <div className="mn-actions">
          <button className="btn-edit" onClick={() => onEdit(node)} title="Edit">✏️</button>
          {depth === 0 && (
            <button className="btn-edit" onClick={() => onRootProducts(node)} title="View all products under this menu">📋</button>
          )}
          {!node.has_products && (
            <button className="btn-edit" onClick={() => onAddChild(node)} title="Add child menu">➕</button>
          )}
          {!node.has_children && (
            <button className="btn-edit" onClick={() => onProducts(node)} title="Manage products">🔗</button>
          )}
          <button className="btn-delete" onClick={() => onDelete(node)} title="Delete">🗑️</button>
        </div>
      </div>
      {isExpanded && hasChildren && node.children.map((child) => (
        <TreeNode
          key={child.id}
          node={child}
          expanded={expanded}
          selectedId={selectedId}
          onToggle={onToggle}
          onEdit={onEdit}
          onAddChild={onAddChild}
          onProducts={onProducts}
          onDelete={onDelete}
          depth={depth + 1}
        />
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Time Window Modal
   ═══════════════════════════════════════════════════════════ */
const TimeWindowModal = ({ initial, menuProductId, menuPackId, productName, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial
      ? { start_time: initial.start_time?.slice(0, 5) || '', end_time: initial.end_time?.slice(0, 5) || '', days_of_week: initial.days_of_week || '', is_active: initial.is_active }
      : { start_time: '', end_time: '', days_of_week: 'AD', is_active: true }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.start_time || !form.end_time) { setError('Start time and end time are required.'); return; }
    if (form.end_time <= form.start_time) { setError('End time must be after start time.'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/menu-admin/time-windows/${initial.id}`, form, { headers: getAuthHeader() });
      } else {
        const linkField = menuProductId ? { menu_product_id: menuProductId } : { menu_pack_id: menuPackId };
        await axios.post(`${API_URL}/menu-admin/time-windows`, { ...form, ...linkField }, { headers: getAuthHeader() });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save time window.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Time Window' : `Add Time Window — ${productName}`}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error"><span className="alert-icon">⚠</span>{error}</div>}
          <div className="form-row">
            <div className="form-group">
              <label>Start Time *</label>
              <input className="form-input" type="time" value={form.start_time}
                onChange={(e) => set('start_time', e.target.value)} required />
            </div>
            <div className="form-group">
              <label>End Time *</label>
              <input className="form-input" type="time" value={form.end_time}
                onChange={(e) => set('end_time', e.target.value)} required />
            </div>
          </div>
          <div className="form-group">
            <label>Days of Week</label>
            <input className="form-input" placeholder="e.g. Mon,Tue,Wed,Thu,Fri"
              value={form.days_of_week} onChange={(e) => set('days_of_week', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input type="checkbox" checked={form.is_active}
                onChange={(e) => set('is_active', e.target.checked)} />
              Active
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update Window' : 'Add Window'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Page Modal
   ═══════════════════════════════════════════════════════════ */
const PageModal = ({ initial, menuId, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial
    ? { ...initial }
    : { page_no: 1, image_url: '', display_text: '', page_type: 'Body', static_banner: 'B', status: 'A',
        cta_type: '', cta_label: '', cta_value: '', cta_message: '' }
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const data = new FormData();
      data.append('image', file);
      const res = await axios.post(`${API_URL}/menu-admin/upload-image`, data, { headers: getAuthHeader() });
      set('image_url', res.data.url);
    } catch {
      setError('Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = { ...form, menu_id: menuId };
      if (isEdit) {
        await axios.put(`${API_URL}/menu-admin/menu-pages/${initial.id}`, payload, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API_URL}/menu-admin/menu-pages`, payload, { headers: getAuthHeader() });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content">
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Page' : 'Add Page'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="modal-error">{error}</div>}
        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label>Page No *</label>
              <input className="form-input" type="number" min="1"
                value={form.page_no}
                onChange={(e) => set('page_no', parseInt(e.target.value, 10) || 1)}
                required />
            </div>
            <div className="form-group">
              <label>Page Type *</label>
              <select className="form-input" value={form.page_type}
                onChange={(e) => set('page_type', e.target.value)} required>
                <option value="Header">Header</option>
                <option value="Body">Body</option>
                <option value="Footer">Footer</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Display Text</label>
            <textarea className="form-input" rows={3}
              value={form.display_text}
              onChange={(e) => set('display_text', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Image</label>
            <input type="file" accept="image/*" onChange={handleImageChange} disabled={uploading} />
            {uploading && <span style={{ fontSize: 12, color: '#999' }}>Uploading…</span>}
            {form.image_url && (
              <img src={resolveImg(form.image_url)} alt="preview"
                style={{ maxHeight: 80, marginTop: 8, borderRadius: 4, display: 'block' }} />
            )}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Static Banner</label>
              <select className="form-input" value={form.static_banner}
                onChange={(e) => set('static_banner', e.target.value)}>
                <option value="B">B — Banner</option>
                <option value="S">S — Static</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select className="form-input" value={form.status}
                onChange={(e) => set('status', e.target.value)}>
                <option value="A">Active</option>
                <option value="I">Inactive</option>
              </select>
            </div>
          </div>
          {/* ── Call to Action ── */}
          <div className="form-group">
            <label>Call to Action</label>
            <select className="form-input" value={form.cta_type || ''}
              onChange={(e) => set('cta_type', e.target.value || null)}>
              <option value="">None</option>
              <option value="url">Open Website</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          {form.cta_type && (
            <div className="form-group">
              <label>Button Label</label>
              <input className="form-input" type="text" placeholder="e.g. Order Now / Chat with us"
                value={form.cta_label || ''}
                onChange={(e) => set('cta_label', e.target.value)} />
            </div>
          )}
          {form.cta_type === 'url' && (
            <div className="form-group">
              <label>Website URL</label>
              <input className="form-input" type="url" placeholder="https://example.com"
                value={form.cta_value || ''}
                onChange={(e) => set('cta_value', e.target.value)} />
            </div>
          )}
          {form.cta_type === 'whatsapp' && (
            <>
              <div className="form-group">
                <label>WhatsApp Phone (with country code, no +)</label>
                <input className="form-input" type="text" placeholder="919876543210"
                  value={form.cta_value || ''}
                  onChange={(e) => set('cta_value', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Prefilled Message</label>
                <textarea className="form-input" rows={3} placeholder="Hi, I'd like to order…"
                  value={form.cta_message || ''}
                  onChange={(e) => set('cta_message', e.target.value)} />
              </div>
            </>
          )}
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving || uploading}>
              {saving ? 'Saving…' : isEdit ? 'Update Page' : 'Add Page'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Pages Panel
   ═══════════════════════════════════════════════════════════ */
const PagesPanel = ({ menu }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);

  const loadPages = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API_URL}/menu-admin/menu-pages`,
        { params: { menu_id: menu.id }, headers: getAuthHeader() });
      setPages(res.data);
    } catch {
      setError('Failed to load pages.');
    } finally {
      setLoading(false);
    }
  }, [menu.id]);

  useEffect(() => { loadPages(); }, [loadPages]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await axios.delete(`${API_URL}/menu-admin/menu-pages/${id}`, { headers: getAuthHeader() });
      loadPages();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed.');
    }
  };

  if (loading) return <div className="loading-spinner">Loading pages…</div>;

  return (
    <div className="mn-products">
      <div className="users-header">
        <div className="header-content">
          <h1>Pages</h1>
          <p>Menu pages for <strong>{menu.menu_name}</strong></p>
        </div>
        <button className="btn-add-user" onClick={() => setModal({ type: 'add' })}>
          <span className="icon">+</span> Add Page
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>{error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Page No</th>
              <th>Type</th>
              <th>Image</th>
              <th>Display Text</th>
              <th>Static Banner</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#999', padding: '24px' }}>
                  No pages added yet.
                </td>
              </tr>
            ) : pages.map(p => (
              <tr key={p.id}>
                <td>{p.page_no}</td>
                <td>
                  <span className={`mn-page-type mn-page-type--${p.page_type.toLowerCase()}`}>
                    {p.page_type}
                  </span>
                </td>
                <td>
                  {p.image_url
                    ? <img src={resolveImg(p.image_url)} alt=""
                        style={{ height: 40, borderRadius: 4, objectFit: 'cover' }} />
                    : <span style={{ color: '#999' }}>—</span>
                  }
                </td>
                <td style={{ maxWidth: 220, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {p.display_text || '—'}
                </td>
                <td style={{ textAlign: 'center' }}>{p.static_banner}</td>
                <td>
                  <span className={`status-badge ${p.status === 'A' ? 'status-active' : 'status-inactive'}`}>
                    {p.status === 'A' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button className="btn-icon-edit" onClick={() => setModal({ type: 'edit', page: p })}>
                    Edit
                  </button>
                  <button className="btn-icon-delete" onClick={() => handleDelete(p.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal?.type === 'add' && (
        <PageModal menuId={menu.id}
          onSave={() => { setModal(null); loadPages(); }}
          onClose={() => setModal(null)} />
      )}
      {modal?.type === 'edit' && (
        <PageModal initial={modal.page} menuId={menu.id}
          onSave={() => { setModal(null); loadPages(); }}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Products Panel (right side)
   ═══════════════════════════════════════════════════════════ */
const ProductsPanel = ({ menu, onReload }) => {
  const [links, setLinks] = useState([]);
  const [productsLov, setProductsLov] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedPrices, setExpandedPrices] = useState(new Set());
  const [pricesMap, setPricesMap] = useState({});
  const [expandedWindows, setExpandedWindows] = useState(new Set());
  const [windowsMap, setWindowsMap] = useState({});
  const [modal, setModal] = useState(null);

  const loadLinks = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API_URL}/menu-admin/menu-products`,
        { params: { menu_id: menu.id }, headers: getAuthHeader() });
      setLinks(res.data);
    } catch {
      setError('Failed to load product links.');
    } finally {
      setLoading(false);
    }
  }, [menu.id]);

  useEffect(() => {
    loadLinks();
    axios.get(`${API_URL}/menu-admin/products-lov`, { headers: getAuthHeader() })
      .then((r) => setProductsLov(r.data))
      .catch(() => {});
  }, [loadLinks]);

  const loadPrices = async (linkId) => {
    try {
      const res = await axios.get(`${API_URL}/menu-admin/prices`,
        { params: { menu_product_id: linkId }, headers: getAuthHeader() });
      setPricesMap((p) => ({ ...p, [linkId]: res.data }));
    } catch {}
  };

  const togglePrices = (linkId) => {
    setExpandedPrices((prev) => {
      const next = new Set(prev);
      if (next.has(linkId)) { next.delete(linkId); }
      else { next.add(linkId); loadPrices(linkId); }
      return next;
    });
  };

  const loadWindows = async (linkId) => {
    try {
      const res = await axios.get(`${API_URL}/menu-admin/time-windows`,
        { params: { menu_product_id: linkId }, headers: getAuthHeader() });
      setWindowsMap((p) => ({ ...p, [linkId]: res.data }));
    } catch {}
  };

  const toggleWindows = (linkId) => {
    setExpandedWindows((prev) => {
      const next = new Set(prev);
      if (next.has(linkId)) { next.delete(linkId); }
      else { next.add(linkId); loadWindows(linkId); }
      return next;
    });
  };

  const handleDeleteWindow = async (windowId, linkId) => {
    if (!window.confirm('Delete this time window?')) return;
    try {
      await axios.delete(`${API_URL}/menu-admin/time-windows/${windowId}`, { headers: getAuthHeader() });
      loadWindows(linkId);
    } catch {}
  };

  const handleDeleteLink = async (id) => {
    if (!window.confirm('Remove this product link and all its prices?')) return;
    try {
      await axios.delete(`${API_URL}/menu-admin/menu-products/${id}`, { headers: getAuthHeader() });
      loadLinks();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed.');
    }
  };

  const handleDeletePrice = async (priceId, linkId) => {
    if (!window.confirm('Delete this price record?')) return;
    try {
      await axios.delete(`${API_URL}/menu-admin/prices/${priceId}`, { headers: getAuthHeader() });
      loadPrices(linkId);
    } catch {}
  };

  if (loading) return <div className="loading-spinner">Loading products…</div>;

  return (
    <div className="mn-products">
      <div className="users-header">
        <div className="header-content">
          <h1>Products</h1>
          <p>Linked to <strong>{menu.menu_name}</strong></p>
        </div>
        <button className="btn-add-user" onClick={() => setModal({ type: 'add-link' })}>
          <span className="icon">+</span> Link Product
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>{error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Description</th>
              <th>Order</th>
              <th style={{ textAlign: 'right' }}>Commission %</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {links.length === 0 ? (
              <tr><td colSpan={6} className="no-data">No products linked to this menu.</td></tr>
            ) : links.map((link) => (
              <>
                <tr key={link.id}>
                  <td>
                    <div className="user-info">
                      {link.product_image_url
                        ? <img src={resolveImg(link.product_image_url)} alt={link.product_name} className="mn-prod-img" />
                        : <span className="user-avatar">P</span>}
                      <span>{link.product_name}</span>
                    </div>
                  </td>
                  <td>{link.description || <span style={{ color: '#bbb' }}>—</span>}</td>
                  <td>{link.display_order}</td>
                  <td style={{ textAlign: 'right' }}>
                    {link.referral_commission_percent != null
                      ? `${parseFloat(link.referral_commission_percent)}%`
                      : <span style={{ color: '#bbb' }}>—</span>}
                  </td>
                  <td>
                    <span className={`status-badge ${link.is_active ? 'status-active' : 'status-inactive'}`}>
                      {link.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-edit" onClick={() => setModal({ type: 'edit-link', link })} title="Edit link">✏️</button>
                      <button className="btn-edit" onClick={() => togglePrices(link.id)} title="Toggle prices">💲</button>
                      <button className="btn-edit" onClick={() => toggleWindows(link.id)} title="Toggle time windows">🕐</button>
                      <button className="btn-delete" onClick={() => handleDeleteLink(link.id)} title="Remove link">🗑️</button>
                    </div>
                  </td>
                </tr>
                {expandedPrices.has(link.id) && (
                  <tr key={`prices-${link.id}`} className="mn-prices-row">
                    <td colSpan={6}>
                      <div className="mn-prices-inner">
                        <div className="mn-prices-bar">
                          <span className="mn-prices-title">Prices for {link.product_name}</span>
                          <button className="btn-add-user btn-add-user--sm"
                            onClick={() => setModal({ type: 'add-price', link })}>
                            + Add Price
                          </button>
                        </div>
                        <table className="users-table mn-prices-table">
                          <thead>
                            <tr>
                              <th>Price</th><th>From</th><th>To</th><th>Available</th><th>Cutoff</th><th>Lead Days</th><th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(pricesMap[link.id] || []).length === 0 ? (
                              <tr><td colSpan={7} className="no-data">No prices defined.</td></tr>
                            ) : (pricesMap[link.id] || []).map((pr) => (
                              <tr key={pr.id}>
                                <td>₹{parseFloat(pr.price).toFixed(2)}</td>
                                <td>{toIST(pr.start_date)}</td>
                                <td>{toIST(pr.end_date)}</td>
                                <td>
                                  <span className={`status-badge ${pr.is_available ? 'status-active' : 'status-inactive'}`}>
                                    {pr.is_available ? 'Yes' : 'No'}
                                  </span>
                                </td>
                                <td>{pr.order_cutoff_time || '—'}</td>
                                <td>{pr.order_lead_days}d</td>
                                <td>
                                  <div className="action-buttons">
                                    <button className="btn-edit" onClick={() => setModal({ type: 'edit-price', price: pr, link })} title="Edit">✏️</button>
                                    <button className="btn-delete" onClick={() => handleDeletePrice(pr.id, link.id)} title="Delete">🗑️</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
                {expandedWindows.has(link.id) && (
                  <tr key={`windows-${link.id}`} className="mn-windows-row">
                    <td colSpan={6}>
                      <div className="mn-windows-inner">
                        <div className="mn-windows-bar">
                          <span className="mn-windows-title">Time Windows for {link.product_name}</span>
                          <button className="btn-add-user btn-add-user--sm"
                            onClick={() => setModal({ type: 'add-window', link })}>
                            + Add Window
                          </button>
                        </div>
                        <table className="users-table mn-windows-table">
                          <thead>
                            <tr>
                              <th>Start</th><th>End</th><th>Days</th><th>Status</th><th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(windowsMap[link.id] || []).length === 0 ? (
                              <tr><td colSpan={5} className="no-data">No time windows defined.</td></tr>
                            ) : (windowsMap[link.id] || []).map((tw) => (
                              <tr key={tw.id}>
                                <td>{tw.start_time?.slice(0, 5)}</td>
                                <td>{tw.end_time?.slice(0, 5)}</td>
                                <td>{tw.days_of_week || '—'}</td>
                                <td>
                                  <span className={`status-badge ${tw.is_active ? 'status-active' : 'status-inactive'}`}>
                                    {tw.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td>
                                  <div className="action-buttons">
                                    <button className="btn-edit" onClick={() => setModal({ type: 'edit-window', window: tw, link })} title="Edit">✏️</button>
                                    <button className="btn-delete" onClick={() => handleDeleteWindow(tw.id, link.id)} title="Delete">🗑️</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {modal?.type === 'add-link' && (
        <LinkModal
          menuId={menu.id}
          menuName={menu.menu_name}
          productsLov={productsLov}
          onSave={() => { setModal(null); loadLinks(); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'edit-link' && (
        <LinkModal
          menuId={menu.id}
          menuName={menu.menu_name}
          initial={modal.link}
          productsLov={productsLov}
          onSave={() => { setModal(null); loadLinks(); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'add-price' && (
        <PriceModal
          menuProductId={modal.link.id}
          productName={modal.link.product_name}
          onSave={() => { setModal(null); loadPrices(modal.link.id); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'edit-price' && (
        <PriceModal
          initial={modal.price}
          menuProductId={modal.link.id}
          productName={modal.link.product_name}
          onSave={() => { setModal(null); loadPrices(modal.link.id); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'add-window' && (
        <TimeWindowModal
          menuProductId={modal.link.id}
          productName={modal.link.product_name}
          onSave={() => { setModal(null); loadWindows(modal.link.id); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'edit-window' && (
        <TimeWindowModal
          initial={modal.window}
          menuProductId={modal.link.id}
          productName={modal.link.product_name}
          onSave={() => { setModal(null); loadWindows(modal.link.id); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════
   Pack Link Modal
   ═══════════════════════════════════════════════════════════ */
const PackLinkModal = ({ menuId, menuName, initial, packsLov, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial
      ? {
          display_order: initial.display_order,
          is_active: initial.is_active,
          description: initial.link_description || '',
          root_menu_id: initial.root_menu_id || '',
          referral_commission_percent: initial.referral_commission_percent ?? '',
        }
      : { pack_id: '', display_order: 0, is_active: true, description: '', root_menu_id: '', referral_commission_percent: '' }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !form.pack_id) { setError('Select a pack.'); return; }
    setSaving(true); setError('');
    try {
      const commissionVal = form.referral_commission_percent !== '' ? parseFloat(form.referral_commission_percent) : null;
      if (isEdit) {
        await axios.put(`${API_URL}/menu-admin/menu-packs/${initial.id}`, {
          display_order: form.display_order,
          is_active: form.is_active,
          description: form.description,
          root_menu_id: form.root_menu_id ? parseInt(form.root_menu_id) : null,
          referral_commission_percent: commissionVal,
        }, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API_URL}/menu-admin/menu-packs`, {
          menu_id: menuId,
          pack_id: parseInt(form.pack_id),
          display_order: form.display_order,
          is_active: form.is_active,
          description: form.description,
          root_menu_id: form.root_menu_id ? parseInt(form.root_menu_id) : null,
          referral_commission_percent: commissionVal,
        }, { headers: getAuthHeader() });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save pack link.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? `Edit Link — ${initial.pack_name}` : `Link Pack to "${menuName}"`}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error"><span className="alert-icon">⚠</span>{error}</div>}
          {!isEdit && (
            <div className="form-group">
              <label>Pack *</label>
              <select className="form-input" value={form.pack_id}
                onChange={(e) => set('pack_id', e.target.value)} required>
                <option value="">— select a pack —</option>
                {packsLov.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} (×{p.multiples_of})</option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group">
            <label>Description</label>
            <input className="form-input" value={form.description}
              onChange={(e) => set('description', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Display Order</label>
              <input className="form-input" type="number" value={form.display_order}
                onChange={(e) => set('display_order', parseInt(e.target.value) || 0)} />
            </div>
            <div className="form-group">
              <label>Root Menu ID</label>
              <input className="form-input" type="number" value={form.root_menu_id}
                readOnly style={{ background: '#f5f5f5', cursor: 'not-allowed', color: '#888' }} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Commission %</label>
              <input className="form-input" type="number" step="0.01" min="0" max="100"
                placeholder="e.g. 5"
                value={form.referral_commission_percent}
                onChange={(e) => set('referral_commission_percent', e.target.value)} />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label className="checkbox-label">
                <input type="checkbox" checked={form.is_active}
                  onChange={(e) => set('is_active', e.target.checked)} />
                Active
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update Link' : 'Add Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Packs Panel
   ═══════════════════════════════════════════════════════════ */
const PacksPanel = ({ menu }) => {
  const [links, setLinks] = useState([]);
  const [packsLov, setPacksLov] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedPrices, setExpandedPrices] = useState(new Set());
  const [pricesMap, setPricesMap] = useState({});
  const [expandedWindows, setExpandedWindows] = useState(new Set());
  const [windowsMap, setWindowsMap] = useState({});
  const [modal, setModal] = useState(null);

  const loadLinks = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API_URL}/menu-admin/menu-packs`,
        { params: { menu_id: menu.id }, headers: getAuthHeader() });
      setLinks(res.data);
    } catch {
      setError('Failed to load pack links.');
    } finally {
      setLoading(false);
    }
  }, [menu.id]);

  useEffect(() => {
    loadLinks();
    axios.get(`${API_URL}/menu-admin/packs-lov`, { headers: getAuthHeader() })
      .then((r) => setPacksLov(r.data))
      .catch(() => {});
  }, [loadLinks]);

  const loadPrices = async (linkId) => {
    try {
      const res = await axios.get(`${API_URL}/menu-admin/prices`,
        { params: { menu_pack_id: linkId }, headers: getAuthHeader() });
      setPricesMap((p) => ({ ...p, [linkId]: res.data }));
    } catch {}
  };

  const togglePrices = (linkId) => {
    setExpandedPrices((prev) => {
      const next = new Set(prev);
      if (next.has(linkId)) { next.delete(linkId); }
      else { next.add(linkId); loadPrices(linkId); }
      return next;
    });
  };

  const loadWindows = async (linkId) => {
    try {
      const res = await axios.get(`${API_URL}/menu-admin/time-windows`,
        { params: { menu_pack_id: linkId }, headers: getAuthHeader() });
      setWindowsMap((p) => ({ ...p, [linkId]: res.data }));
    } catch {}
  };

  const toggleWindows = (linkId) => {
    setExpandedWindows((prev) => {
      const next = new Set(prev);
      if (next.has(linkId)) { next.delete(linkId); }
      else { next.add(linkId); loadWindows(linkId); }
      return next;
    });
  };

  const handleDeletePrice = async (priceId, linkId) => {
    if (!window.confirm('Delete this price record?')) return;
    try {
      await axios.delete(`${API_URL}/menu-admin/prices/${priceId}`, { headers: getAuthHeader() });
      loadPrices(linkId);
    } catch {}
  };

  const handleDeleteWindow = async (windowId, linkId) => {
    if (!window.confirm('Delete this time window?')) return;
    try {
      await axios.delete(`${API_URL}/menu-admin/time-windows/${windowId}`, { headers: getAuthHeader() });
      loadWindows(linkId);
    } catch {}
  };

  const handleDeleteLink = async (id) => {
    if (!window.confirm('Remove this pack from the menu?')) return;
    try {
      await axios.delete(`${API_URL}/menu-admin/menu-packs/${id}`, { headers: getAuthHeader() });
      loadLinks();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed.');
    }
  };

  const unlinkedPacks = packsLov.filter((p) => !links.some((l) => l.pack_id === p.id));

  if (loading) return <div className="loading-spinner">Loading packs…</div>;

  return (
    <div className="mn-products">
      <div className="users-header">
        <div className="header-content">
          <h1>Packs</h1>
          <p>Linked to <strong>{menu.menu_name}</strong></p>
        </div>
        <button className="btn-add-user" onClick={() => setModal({ type: 'add-link' })}>
          <span className="icon">+</span> Link Pack
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>{error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Pack</th>
              <th>Description</th>
              <th>Multiples Of</th>
              <th>Groups</th>
              <th>Order</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {links.length === 0 ? (
              <tr><td colSpan={7} className="no-data">No packs linked to this menu.</td></tr>
            ) : links.map((lnk) => (
              <>
                <tr key={lnk.id}>
                  <td>
                    <div className="user-info">
                      <span className="user-avatar">🎁</span>
                      <span>{lnk.pack_name}</span>
                    </div>
                  </td>
                  <td>{lnk.description || <span style={{ color: '#bbb' }}>—</span>}</td>
                  <td>{lnk.multiples_of}</td>
                  <td>{lnk.group_count}</td>
                  <td>{lnk.display_order}</td>
                  <td>
                    <span className={`status-badge ${lnk.is_active ? 'status-active' : 'status-inactive'}`}>
                      {lnk.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-edit" onClick={() => setModal({ type: 'edit-link', link: lnk })} title="Edit link">✏️</button>
                      <button className="btn-edit" onClick={() => togglePrices(lnk.id)} title="Toggle prices">💲</button>
                      <button className="btn-edit" onClick={() => toggleWindows(lnk.id)} title="Toggle time windows">🕐</button>
                      <button className="btn-delete" onClick={() => handleDeleteLink(lnk.id)} title="Remove link">🗑️</button>
                    </div>
                  </td>
                </tr>
                {expandedPrices.has(lnk.id) && (
                  <tr key={`prices-${lnk.id}`} className="mn-prices-row">
                    <td colSpan={7}>
                      <div className="mn-prices-inner">
                        <div className="mn-prices-bar">
                          <span className="mn-prices-title">Prices for {lnk.pack_name}</span>
                          <button className="btn-add-user btn-add-user--sm"
                            onClick={() => setModal({ type: 'add-price', link: lnk })}>
                            + Add Price
                          </button>
                        </div>
                        <table className="users-table mn-prices-table">
                          <thead>
                            <tr>
                              <th>Price</th><th>From</th><th>To</th><th>Available</th><th>Cutoff</th><th>Lead Days</th><th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(pricesMap[lnk.id] || []).length === 0 ? (
                              <tr><td colSpan={7} className="no-data">No prices defined.</td></tr>
                            ) : (pricesMap[lnk.id] || []).map((pr) => (
                              <tr key={pr.id}>
                                <td>₹{parseFloat(pr.price).toFixed(2)}</td>
                                <td>{toIST(pr.start_date)}</td>
                                <td>{toIST(pr.end_date)}</td>
                                <td>
                                  <span className={`status-badge ${pr.is_available ? 'status-active' : 'status-inactive'}`}>
                                    {pr.is_available ? 'Yes' : 'No'}
                                  </span>
                                </td>
                                <td>{pr.order_cutoff_time || '—'}</td>
                                <td>{pr.order_lead_days}d</td>
                                <td>
                                  <div className="action-buttons">
                                    <button className="btn-edit" onClick={() => setModal({ type: 'edit-price', price: pr, link: lnk })} title="Edit">✏️</button>
                                    <button className="btn-delete" onClick={() => handleDeletePrice(pr.id, lnk.id)} title="Delete">🗑️</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
                {expandedWindows.has(lnk.id) && (
                  <tr key={`windows-${lnk.id}`} className="mn-windows-row">
                    <td colSpan={7}>
                      <div className="mn-windows-inner">
                        <div className="mn-windows-bar">
                          <span className="mn-windows-title">Time Windows for {lnk.pack_name}</span>
                          <button className="btn-add-user btn-add-user--sm"
                            onClick={() => setModal({ type: 'add-window', link: lnk })}>
                            + Add Window
                          </button>
                        </div>
                        <table className="users-table mn-windows-table">
                          <thead>
                            <tr>
                              <th>Start</th><th>End</th><th>Days</th><th>Status</th><th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(windowsMap[lnk.id] || []).length === 0 ? (
                              <tr><td colSpan={5} className="no-data">No time windows defined.</td></tr>
                            ) : (windowsMap[lnk.id] || []).map((tw) => (
                              <tr key={tw.id}>
                                <td>{tw.start_time?.slice(0, 5)}</td>
                                <td>{tw.end_time?.slice(0, 5)}</td>
                                <td>{tw.days_of_week || '—'}</td>
                                <td>
                                  <span className={`status-badge ${tw.is_active ? 'status-active' : 'status-inactive'}`}>
                                    {tw.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td>
                                  <div className="action-buttons">
                                    <button className="btn-edit" onClick={() => setModal({ type: 'edit-window', window: tw, link: lnk })} title="Edit">✏️</button>
                                    <button className="btn-delete" onClick={() => handleDeleteWindow(tw.id, lnk.id)} title="Delete">🗑️</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {modal?.type === 'add-link' && (
        <PackLinkModal
          menuId={menu.id}
          menuName={menu.menu_name}
          packsLov={unlinkedPacks}
          onSave={() => { setModal(null); loadLinks(); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'edit-link' && (
        <PackLinkModal
          menuId={menu.id}
          menuName={menu.menu_name}
          initial={modal.link}
          packsLov={packsLov}
          onSave={() => { setModal(null); loadLinks(); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'add-price' && (
        <PriceModal
          menuPackId={modal.link.id}
          productName={modal.link.pack_name}
          onSave={() => { setModal(null); loadPrices(modal.link.id); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'edit-price' && (
        <PriceModal
          initial={modal.price}
          menuPackId={modal.link.id}
          productName={modal.link.pack_name}
          onSave={() => { setModal(null); loadPrices(modal.link.id); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'add-window' && (
        <TimeWindowModal
          menuPackId={modal.link.id}
          productName={modal.link.pack_name}
          onSave={() => { setModal(null); loadWindows(modal.link.id); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'edit-window' && (
        <TimeWindowModal
          initial={modal.window}
          menuPackId={modal.link.id}
          productName={modal.link.pack_name}
          onSave={() => { setModal(null); loadWindows(modal.link.id); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
const MenuAdmin = () => {
  const { user } = useContext(AuthContext);

  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [detailTab, setDetailTab] = useState('products');
  const [rootProductsMenu, setRootProductsMenu] = useState(null);
  const [modal, setModal] = useState(null);

  const loadMenus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/menu-admin/menus`, { headers: getAuthHeader() });
      setMenus(res.data);
    } catch {
      setError('Failed to load menus.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMenus(); }, [loadMenus]);

  const tree = buildTree(menus);

  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const handleEdit = (node) => setModal({ type: 'edit-menu', menu: node });
  const handleAddChild = (node) => setModal({ type: 'add-menu', parentId: node.id, parentName: node.menu_name });
  const handleAddRoot = () => setModal({ type: 'add-menu', parentId: null, parentName: null });
  const handleProducts = (node) => { setSelectedMenu(node); setDetailTab('products'); };
  const handleRootProducts = (node) => setRootProductsMenu(node);

  const handleDelete = async (node) => {
    if (node.has_children) {
      setError(`Cannot delete "${node.menu_name}": it has sub-menus.`);
      return;
    }
    if (node.has_products) {
      setError(`Cannot delete "${node.menu_name}": it has product associations.`);
      return;
    }
    if (!window.confirm(`Delete menu "${node.menu_name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/menu-admin/menus/${node.id}`, { headers: getAuthHeader() });
      if (selectedMenu?.id === node.id) setSelectedMenu(null);
      setSuccess(`Menu "${node.menu_name}" deleted.`);
      loadMenus();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed.');
    }
  };

  const onMenuSaved = () => {
    setModal(null);
    setSuccess('Menu saved successfully.');
    loadMenus();
    setTimeout(() => setSuccess(''), 4000);
  };

  return (
    <div className="users-management">
      <div className="users-header">
        <div className="header-content">
          <h1>Menu Administration</h1>
          <p>Manage menus, sub-menus and product associations</p>
        </div>
        <button className="btn-add-user" onClick={handleAddRoot}>
          <span className="icon">+</span> Add Root Menu
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>{error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>{success}
          <button className="alert-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      <div className="mn-layout">
        {/* Tree panel */}
        <div className="mn-tree-panel">
          <div className="mn-tree-header">Menu Tree</div>
          {loading
            ? <div className="loading-spinner">Loading…</div>
            : tree.length === 0
              ? <p className="no-data">No menus found.</p>
              : tree.map((node) => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    expanded={expanded}
                    selectedId={selectedMenu?.id}
                    onToggle={toggleExpand}
                    onEdit={handleEdit}
                    onAddChild={handleAddChild}
                    onProducts={handleProducts}
                    onRootProducts={handleRootProducts}
                    onDelete={handleDelete}
                    depth={0}
                  />
                ))
          }
        </div>

        {/* Detail panel */}
        <div className="mn-detail-panel">
          {selectedMenu ? (
            <>
              <div className="mn-detail-tabs">
                <button
                  className={`mn-detail-tab${detailTab === 'products' ? ' mn-detail-tab--active' : ''}`}
                  onClick={() => setDetailTab('products')}
                >
                  Products
                </button>
                <button
                  className={`mn-detail-tab${detailTab === 'pages' ? ' mn-detail-tab--active' : ''}`}
                  onClick={() => setDetailTab('pages')}
                >
                  Pages
                </button>
                <button
                  className={`mn-detail-tab${detailTab === 'packs' ? ' mn-detail-tab--active' : ''}`}
                  onClick={() => setDetailTab('packs')}
                >
                  Packs
                </button>
              </div>
              {detailTab === 'products'
                ? <ProductsPanel key={`prod-${selectedMenu.id}`} menu={selectedMenu} onReload={loadMenus} />
                : detailTab === 'pages'
                  ? <PagesPanel key={`pg-${selectedMenu.id}`} menu={selectedMenu} />
                  : <PacksPanel key={`packs-${selectedMenu.id}`} menu={selectedMenu} />
              }
            </>
          ) : (
            <div className="mn-idle">
              <div className="mn-idle-icon">🗂️</div>
              <p>Select a menu from the tree to manage its products.</p>
              <p>Use the action buttons on each menu row to edit, add children, or delete.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {modal?.type === 'add-menu' && (
        <MenuModal
          parentId={modal.parentId}
          parentName={modal.parentName}
          onSave={onMenuSaved}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'edit-menu' && (
        <MenuModal
          initial={modal.menu}
          parentId={modal.menu.parent_id}
          parentName={modal.menu.parent_name}
          onSave={onMenuSaved}
          onClose={() => setModal(null)}
        />
      )}
      {rootProductsMenu && (
        <RootProductsPanel
          rootMenu={rootProductsMenu}
          onClose={() => setRootProductsMenu(null)}
        />
      )}
    </div>
  );
};

export default MenuAdmin;
